'use client';

import { useEffect, useState } from 'react';
import { formatZoneDate, formatZoneTime, localTimeZone } from '@/lib/browser-time-tools-extra';

export default function BrowserOnlineClock() {
  const [now, setNow] = useState<Date | null>(null);
  const [zone, setZone] = useState('UTC');
  const [hour12, setHour12] = useState(false);
  const [showSeconds, setShowSeconds] = useState(true);

  useEffect(() => {
    setNow(new Date());
    setZone(localTimeZone());
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const seconds = now ? now.getSeconds() : 0;
  const minutes = now ? now.getMinutes() : 0;
  const hours = now ? now.getHours() % 12 : 0;

  const secondAngle = seconds * 6;
  const minuteAngle = minutes * 6 + seconds * 0.1;
  const hourAngle = hours * 30 + minutes * 0.5;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Online clock</span>
        <h2 className="tool-live-title">Online clock</h2>
        <p className="tool-live-subtitle">
          A live digital and analog clock for your screen. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Hour format</span>
              <select
                onChange={(event) => setHour12(event.target.value === '12')}
                value={hour12 ? '12' : '24'}
              >
                <option value="24">24-hour</option>
                <option value="12">12-hour</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Seconds</span>
              <select
                onChange={(event) => setShowSeconds(event.target.value === 'on')}
                value={showSeconds ? 'on' : 'off'}
              >
                <option value="on">Show seconds</option>
                <option value="off">Hide seconds</option>
              </select>
            </label>
          </div>
          <p className="tool-live-panel-note">
            Tip: use your browser&apos;s full-screen mode for a desk clock. Time runs locally.
          </p>
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 20,
              padding: '8px 0',
            }}
          >
            <svg
              aria-hidden="true"
              height={200}
              viewBox="0 0 200 200"
              width={200}
              style={{ maxWidth: '100%' }}
            >
              <circle
                cx={100}
                cy={100}
                r={96}
                fill="none"
                stroke="rgba(148, 163, 184, 0.5)"
                strokeWidth={2}
              />
              {Array.from({ length: 12 }).map((_, index) => {
                const angle = (index * 30 * Math.PI) / 180;
                const inner = 82;
                const outer = 92;
                return (
                  <line
                    key={index}
                    x1={100 + inner * Math.sin(angle)}
                    y1={100 - inner * Math.cos(angle)}
                    x2={100 + outer * Math.sin(angle)}
                    y2={100 - outer * Math.cos(angle)}
                    stroke="rgba(148, 163, 184, 0.7)"
                    strokeWidth={index % 3 === 0 ? 3 : 1.5}
                  />
                );
              })}
              {now !== null ? (
                <>
                  <line
                    x1={100}
                    y1={100}
                    x2={100 + 48 * Math.sin((hourAngle * Math.PI) / 180)}
                    y2={100 - 48 * Math.cos((hourAngle * Math.PI) / 180)}
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth={5}
                  />
                  <line
                    x1={100}
                    y1={100}
                    x2={100 + 72 * Math.sin((minuteAngle * Math.PI) / 180)}
                    y2={100 - 72 * Math.cos((minuteAngle * Math.PI) / 180)}
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth={3}
                  />
                  {showSeconds ? (
                    <line
                      x1={100}
                      y1={100}
                      x2={100 + 80 * Math.sin((secondAngle * Math.PI) / 180)}
                      y2={100 - 80 * Math.cos((secondAngle * Math.PI) / 180)}
                      stroke="#dc2626"
                      strokeLinecap="round"
                      strokeWidth={1.5}
                    />
                  ) : null}
                  <circle cx={100} cy={100} r={4} fill="currentColor" />
                </>
              ) : null}
            </svg>

            <p
              className="tool-live-title"
              style={{ fontSize: 44, fontVariantNumeric: 'tabular-nums', margin: 0 }}
            >
              {now === null ? '--:--' : formatZoneTime(now, zone, { hour12, seconds: showSeconds })}
            </p>
            <p className="tool-live-panel-note" style={{ margin: 0 }}>
              {now === null ? '' : formatZoneDate(now, zone)}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
